<template>
  <a-modal
    class="attach-file-modal edit"
    :title="modalTitle"
    :visible="visible"
    :maskClosable="false"
    centered
    @cancel="handleCancel"
  >
    <!-- custom footer -->
    <template v-slot:footer>
      <a-button key="back" @click="handleCancel">{{$t("Common_Cancel")}}</a-button>
      <a-button
        key="submit"
        type="primary"
        :loading="loading"
        @click="handleOk"
      >
        {{ mode === "edit" ? $t("Common_Modify") : $t("Common_Enroll")}}
      </a-button>
    </template>

    <!-- contents -->
    <div class="content-group">
      <div class="require-text">* {{$t("Attach_Upload1")}}</div>
      <div class="content-item">
        <div class="modal-title">{{$t("Common_Title")}}*</div>
        <input class="modal-input" type="text" v-model="attachSubject" />
      </div>

      <div class="content-item">
        <div class="modal-title">{{$t("Attach_Upload3")}}*</div>
        <div class="modal-value">
          <div class="attach-file-type">
            <div
              v-for="(attachExam, index) in attachExamTypeList"
              :key="index"
              class="file-button"
              :class="{ active: attachExamType === attachExam.value }"
              @click="() => selectAttachExamType(attachExam)"
            >
              <img :src="attachExam.icon" :alt="attachExam.title" />
              <div class="file-title">{{ attachExam.title }}</div>
              <div class="file-sub-title">{{ attachExam.subTitle }}</div>
              <div class="file-contents" v-html="attachExam.contents"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="content-item">
        <div class="modal-title">{{$t("Attach_Upload4")}}*</div>
        <div class="modal-value">
          <div class="attach-file-category">
            <div
              v-for="(category, index) in attachCategoryList"
              :key="index"
              class="file-button"
              :class="{ active: attachCategory === category.value }"
              @click="() => selectAttachCategory(category)"
            >
              <div class="file-contents">
                {{ category.contents }}
              </div>
              <i class="check"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="content-item">
        <div class="modal-title">{{$t("Attach_Upload5")}}</div>
        <a-select
          :value="attachFunction"
          mode="tags"
          :placeholder="$t('Attach_Upload6')"
          @change="(value) => handleSelectTag('attachFunction', value)"
        >
          <a-select-option
            v-for="(item, index) in attachFuncList"
            :key="index"
            :value="item.value"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div class="content-item">
        <div class="modal-title">{{$t("Attach_Upload7")}}</div>
        <a-select
          :value="attachRestrict"
          mode="tags"
          style="width: 100%"
          :placeholder="$t('Attach_Upload8')"
          @change="(value) => handleSelectTag('attachRestrict', value)"
        >
          <a-select-option
            v-for="(item, index) in attachRestrictList"
            :key="index"
            :value="item.value"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div class="content-item">
        <form class="file-upload-form">
          <div
            v-for="(item, index) in fileUploadList"
            :key="index"
            class="file-item"
          >
            <div class="modal-title">{{ item.title }}</div>
            <div class="file-input">
              <div
                v-if="!!item.target.name"
                class="file"
                :class="{ selected: item.target.name }"
              >
                <img
                  src="/img/common/close_black.png"
                  alt="delete"
                  class="delete-file-icon"
                  :title="$t('Attach_Upload9')"
                  @click="(e) => handleResetFile(item.targetName)"
                />
                <FileOutlined class="file-image" />
                <div class="file-text-area">
                  <div class="file-name">{{ item.target.name }}</div>
                  <div v-if="!!item.target.file" class="file-size">
                    {{ convertFileSize(item.target.file.size) }}
                  </div>
                </div>
              </div>
              <div v-else class="file">
                <img src="/img/attach/upload.png" alt="upload-icon" />
                <span class="file-text-area">
                  {{$t("Attach_Upload10")}}
                </span>
              </div>
              <input
                :id="index"
                type="file"
                :accept="item.acceptFile"
                @change="(e) => handleFile(e, item.targetName)"
                @click="(e) => (e.target.value = null)"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { toRefs, ref, reactive, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { apiUrl } from "@/utils/common";
import InputComponent from "@/components/commonComponents/InputComponent";
import { FileOutlined } from "@ant-design/icons-vue";
import { useI18n } from "vue-i18n";

export default {
  name: "AttachFileUploadEditModal",
  props: {
    mode: String,
    visible: Boolean,
    toggleModal: Function,
    attachInfo: Object,
  },
  components: {
    InputComponent,
    FileOutlined,
  },
  setup(props) {
    const store = useStore();
    const { t } = useI18n();
    const inputValues = computed(
      () => store.getters["attachFile/GET_INPUT_VALUES"]
    );
    const modalTitle =
      props.mode === "edit" ? t("Attach_Upload11") : t("Attach_Upload12");

    const state = reactive({
      loading: false,
    });

    const formState = reactive({
      attachSubject: props.mode === "edit" ? props.attachInfo.subject : "",
      attachExamType:
        props.mode === "edit" ? props.attachInfo.attachExamType : 0,
      attachCategory:
        props.mode === "edit" ? props.attachInfo.attachCategory : 0,
      attachFunction:
        props.mode === "edit"
          ? Object.keys(props.attachInfo.attachFunction).map((item) =>
              item.toUpperCase()
            )
          : [],
      attachRestrict:
        props.mode === "edit"
          ? Object.keys(props.attachInfo.attachRestrict).map((item) =>
              item.toUpperCase()
            )
          : [],
      attachFile: {
        name: props.mode === "edit" ? props.attachInfo.fileName : "",
        file: null,
      },
      fileIcon: {
        name: props.mode === "edit" ? props.attachInfo.fileIcon : "",
        file: null,
      },
      actionImage: {
        name: props.mode === "edit" ? props.attachInfo.actionImage : "",
        file: null,
      },
    });

    // 첨부파일 훈련 유형
    const attachExamTypeList = [
      {
        icon: "/img/template/exam_warning.png",
        title: t('Common_attach_title1'),
        subTitle: t("Common_attach_title2"),
        contents:
          t("Common_attach_content1"),
        value: 1,
      },
      {
        icon: "/img/template/exam_file.png",
        title: t("Common_attach_title3"),
        subTitle: t("Common_attach_title4"),
        contents:
          t("Common_attach_content2"),
        value: 2,
      },
      {
        icon: "/img/template/exam_cheat.png",
        title: t("Common_attach_title6"),
        subTitle: t("Common_attach_title7"),
        contents:
          t("Common_attach_content3"),
        value: 3,
      },
    ];

    // 첨부파일 구분
    const attachCategoryList = [
      {
        contents: t("Attach_Upload13"),
        value: 1,
      },
      {
        contents: t("Attach_Upload15"),
        value: 2,
      },
      {
        contents: t("Attach_Upload16"),
        value: 3,
      },
      {
        contents: t("Attach_Upload17"),
        value: 4,
      },
    ];

    // 부가 기능
    const attachFuncList = [
      {
        value: "PG",
        text: "PG(PC Gather) - "+ t("Attach_Upload18"),
      },
      {
        value: "LG",
        text: "LG(LeakFile Gather) - "+ t("Attach_Upload19"),
      },
      {
        value: "PL",
        text: "PL(PC Lock) - "+ t("Attach_Upload20"),
      },
      {
        value: "VS",
        text: "VS(Vaccine Step) - "+ t("Attach_Upload21"),
      },
    ];

    // 제약 사항
    const attachRestrictList = [
      {
        value: "UAC",
        text:
          "UAC(User Account Control) - "+ t("Attach_Upload22"),
      },
      {
        value: "SS",
        text: "SS(Smart Screen) - "+ t("Attach_Upload23"),
      },
      {
        value: "SF",
        text: "SF(Spam Filter) - "+ t("Attach_Upload24"),
      },
      {
        value: "VD",
        text: "VD(Vaccine Detect) - "+ t("Attach_Upload25"),
      },
      {
        value: "LC",
        text: "LC(Link Click) - "+ t("Attach_Upload26"),
      },
      {
        value: "UC",
        text: "UC(User Control) - "+ t("Attach_Upload27"),
      },
      {
        value: "DW",
        text: "DW(Download Warning) - "+ t("Attach_Upload28"),
      },
    ];

    // 파일 업로드 리스트
    const fileUploadList = computed(() => [
      {
        title: t("Attach_Upload13")+"*",
        target: formState.attachFile,
        targetName: "attachFile",
        acceptFile: "*",
      },
      {
        title: t("Attach_Upload29")+"*",
        target: formState.fileIcon,
        targetName: "fileIcon",
        acceptFile: "image/*",
      },
      {
        title: t("Attach_Upload30")+"*",
        target: formState.actionImage,
        targetName: "actionImage",
        acceptFile: "image/*",
      },
    ]);

    onMounted(() => {
      store.dispatch("attachFile/SET_INPUT_VALUE", {
        ...formState,
      });
    });

    onUnmounted(() => {
      // 입력 값 초기화
      store.dispatch("attachFile/INIT_INPUT_VALUE");
    });

    // 첨부파일 훈련 유형 선택 이벤트
    const selectAttachExamType = (attachExam) => {
      formState["attachExamType"] = attachExam.value;
    };

    // 첨부파일 구분 선택 이벤트
    const selectAttachCategory = (category) => {
      formState["attachCategory"] = category.value;
    };

    // 파일 크기 변환
    const convertFileSize = (size) => {
      if (size < 1) return "0 B";

      const fileUnits = ["B", "KB", "MB", "GB", "TB", "PB"];
      const unitIndex = Math.floor(Math.log(size) / Math.log(1024));
      const fileSize = (size / Math.pow(1024, unitIndex)).toFixed(2) * 1;
      return `${fileSize} ${fileUnits[unitIndex]}`;
    };

    // 드롭다운 태그 선택 이벤트
    const handleSelectTag = (name, value) => {
      formState[name] = value;
    };

    // 파일 선택 이벤트
    const handleFile = (e, fileType) => {
      if (e.target.files[0]) {
        formState[fileType] = {
          name: e.target.files[0].name,
          file: e.target.files[0],
        };
      }
    };

    //  선택한 파일 삭제 이벤트
    const handleResetFile = (fileType) => {
      formState[fileType] = { name: "", file: null };
    };

    // 필수 항목 입력 여부 체크
    const checkValid = (target) => {
      let disableArray = [];
      Object.keys(target)
        .filter((key) => key !== "attachFunction" && key !== "attachRestrict")
        .map((key) => {
          if (
            key === "attachFile" ||
            key === "fileIcon" ||
            key === "actionImage"
          ) {
            !formState[key].name ? disableArray.push(key) : false;
          } else {
            !formState[key] ? disableArray.push(key) : false;
          }
        });

      return disableArray.length < 1;
    };

    // 첨부파일 취소 이벤트
    const handleCancel = () => {
      props.toggleModal();
    };

    // 첨부파일 등록/수정 이벤트
    const handleOk = async () => {
      // 폼 데이터 생성
      const formData = new FormData();

      // 첨부파일 수정
      if (props.mode === "edit") {
        if (checkValid(formState) === false) return;

        // 기존과 값이 다른 항목만 파라미터에 넣어줌
        const paramValues = {};
        Object.keys(inputValues.value).map((key) => {
          if (formState[key] !== inputValues.value[key]) {
            if (
              key === "attachFile" ||
              key === "fileIcon" ||
              key === "actionImage"
            ) {
              if (!!formState[key].file) {
                paramValues[key] = formState[key].file;
              }
            } else if (key === "attachFunction" || key === "attachRestrict") {
              const formattedParams = {};
              formState[key].map((item) => {
                formattedParams[item] = 1;
              });
              paramValues[key] = JSON.stringify(formattedParams);
            } else {
              paramValues[key] = formState[key];
            }
          }
        });

        // 변경된 사항이 있을 경우에만 api 호출
        if (Object.keys(paramValues).length > 0) {
          state.loading = true;

          // 폼 데이터에 파라미터 추가
          formData.append("attachInfoNo", props.attachInfo.attachInfoNo);
          for (const key in paramValues) {
            formData.append(key, paramValues[key]);
          }

          try {
            const response = await axios.put(apiUrl + "AttachFile", formData);
            if (response && !response.data.error) {
              store.dispatch("attachFile/UPDATE_ATTACH_FILE", response);
            }
          } catch (error) {
            console.log("attach edit error", error);
          }
          state.loading = false;
        }

        props.toggleModal();
      }
      // 첨부파일 등록
      else {
        if (checkValid(formState) === false) return;

        state.loading = true;

        // 부가 기능 파라미터 형식에 맞게 변경
        const attachFuncParam = {};
        formState.attachFunction.map((item, i) => {
          attachFuncParam[item] = 1;
        });

        // 제약 사항 파라미터 형식에 맞게 변경
        const attachRestrictParam = {};
        formState.attachRestrict.map((item, i) => {
          attachRestrictParam[item] = 1;
        });

        const paramValues = {
          attachSubject: formState.attachSubject,
          attachExamType: formState.attachExamType,
          attachCategory: formState.attachCategory,
          attachFunction: JSON.stringify(attachFuncParam),
          attachRestrict: JSON.stringify(attachRestrictParam),
          attachFile: formState.attachFile.file,
          fileIcon: formState.fileIcon.file,
          actionImage: formState.actionImage.file,
        };

        // 폼 데이터에 파라미터 추가
        for (const key in paramValues) {
          formData.append(key, paramValues[key]);
        }

        try {
          const response = await axios.post(apiUrl + "AttachFile", formData);
          if (response && !response.data.error) {
            store.dispatch("attachFile/UPLOAD_ATTACH_FILE", response);
          }
        } catch (error) {
          console.log("attach upload error", error);
        }

        state.loading = false;
        props.toggleModal();
      }
    };

    return {
      ...toRefs(state),
      ...toRefs(formState),
      modalTitle,
      attachExamTypeList,
      attachCategoryList,
      attachFuncList,
      attachRestrictList,
      fileUploadList,
      selectAttachExamType,
      selectAttachCategory,
      convertFileSize,
      handleSelectTag,
      handleFile,
      handleResetFile,
      handleOk,
      handleCancel,
    };
  },
};
</script>